import { throwIfAborted } from '../abortSignal';
import { XhrError } from '../error';
import { requestJson, RequestError } from '../request';
import { acceptedIdentity } from './identity';
import { withQueueRecordOwnership } from './ownership';

export const FEEDBACK_TIMEOUT_MS = 30_000;

function assertApiEnvironment(record, apiUrl) {
  if (!apiUrl || record.apiUrl !== apiUrl) {
    throw new RequestError('environment', 'Queue API environment does not match');
  }
}

function parseBody(response) {
  try {
    return JSON.parse(response.responseText) || {};
  } catch {
    // HTTP 200 is the acknowledgement even if optional response data is invalid.
    return {};
  }
}

/** One POST, using only a captured snapshot and an attempt-local captcha token. */
export async function sendFeedbackRecord(record, { apiUrl, signal, captchaToken, beginPost } = {}) {
  assertApiEnvironment(record, apiUrl);
  throwIfAborted(signal);

  const headers = { ...record.headers };

  if (record.captcha.required) {
    // Provider selection/fallback belongs to preparation, never to a POST retry.
    const token = typeof captchaToken === 'string' ? captchaToken : captchaToken?.token;
    const provider = typeof captchaToken === 'string' ? record.captcha.provider : captchaToken?.provider;

    if (typeof token !== 'string' || !token) {
      throw new RequestError('captcha', 'A fresh captcha token is required');
    }

    if (provider === 'altcha-v3-sha256') {
      headers['X-ALTCHA-TOKEN'] = token;
    } else if (provider === 'recaptcha') {
      headers['X-RECAPTCHA-TOKEN'] = token;
      headers['X-RECAPTCHA-ACTION'] = 'feedback_send';
    } else {
      throw new RequestError('captcha', 'Captcha provider is not configured');
    }
  }

  const body = JSON.stringify(record.payload);

  if (beginPost && !(await beginPost())) return { status: 'skipped', reason: 'obsolete' };

  throwIfAborted(signal);

  const response = await requestJson(`${record.apiUrl.replace(/\/$/, '')}/v1/feedbacks`, {
    method: 'POST',
    headers,
    body,
    rawResponse: true,
    timeout: FEEDBACK_TIMEOUT_MS,
    signal,
  });

  if (response.status !== 200) {
    const error = new XhrError(response);
    error.kind = 'protocol';

    throw error;
  }

  const responseBody = parseBody(response);

  return {
    status: 'submitted',
    ...acceptedIdentity(record, responseBody),
    submissionId: record.submissionId,
    recordId: record.id ?? null,
    revision: record.revision,
    response,
    body: responseBody,
    cleanupErrors: [],
  };
}

function reportCleanupError(result, operation, error) {
  result.cleanupErrors.push({ operation, name: error?.name || 'Error' });
}

/** Prepares a durable offline record, commits its POST guard, then sends once. */
export async function sendQueueRecord(id, { apiUrl, signal, getCaptchaToken, onFailure } = {}) {
  if (globalThis.navigator?.onLine === false) return { status: 'skipped', reason: 'offline' };

  let accepted;

  try {
    const owned = await withQueueRecordOwnership(
      id,
      async (record, context) => {
        let postStarted = false;

        try {
          assertApiEnvironment(record, apiUrl);

          if (globalThis.navigator?.onLine === false) return { status: 'skipped', reason: 'offline' };

          const captchaToken = record.captcha.required
            ? await getCaptchaToken?.({ ...record.captcha }, { signal: context.signal })
            : undefined;

          throwIfAborted(context.signal);

          if (globalThis.navigator?.onLine === false) return { status: 'skipped', reason: 'offline' };

          const result = await sendFeedbackRecord(record, {
            apiUrl,
            signal: context.signal,
            captchaToken,
            beginPost: async () => {
              postStarted = await context.beginPost();

              return postStarted;
            },
          });

          if (result.status !== 'submitted') return result;

          accepted = result;

          try {
            await context.acknowledge(result.body);
          } catch (error) {
            reportCleanupError(accepted, 'delete', error);
          }

          return accepted;
        } catch (error) {
          // Persist the failure while this exact revision is still owned. A
          // cancellation or offline preparation is not a failure of the record.
          if (onFailure && !context.signal.aborted && (postStarted || globalThis.navigator?.onLine !== false)) {
            await onFailure({ record, error, postStarted, update: context.update });
          }

          throw error;
        }
      },
      { signal },
    );

    if (accepted) {
      if (owned.releaseError) reportCleanupError(accepted, 'release', owned.releaseError);

      return accepted;
    }

    return owned.acquired ? owned.value : { status: 'skipped', reason: 'unavailable' };
  } catch (error) {
    // A late abort or lost lease after HTTP 200 cannot undo server acceptance.
    if (!accepted) throw error;

    reportCleanupError(accepted, 'ownership', error);

    return accepted;
  }
}
